import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { find } from 'lodash';
import { FocusableParent } from '../navigation';
import SpatialNavigationWrapper from '../navigation/SpatialNavigationWrapper';
import LeftArrowIcon from '../Icons/arrow';
import DeleteIcon from '../Icons/delete';
import defaultLayouts, { emailLayouts, noEmbeddedInputLayouts, searchLayouts } from './languages';
import Key from './Key';
import i18n from '../../i18n';
import Platform from '../../platform';
import {
  RightArrowIcon,
  Row,
  StyledCursor,
  StyledInput,
  StyledLetter,
} from 'components/Keyboard/StyledComponents';

const languages = [
  { id: 'uk', title: 'Українська' },
  { id: 'en', title: 'English' },
  { id: 'ru', title: 'Русский' },
];

const getInitalLangId = (languages, id) => {
  const lang = find(languages, { id }) || languages[0];
  return lang.id;
};

const IS_MULTI_LANGUAGE_ALLOWED = process.env.REACT_APP_MULTI_LANGUAGE_ALLOWED === 'true';

/**
 * Эта функция нужна, чтобы посчитать начальную позию курсора,
 * клавиатура открыта с предустановленым query
 * @param {string} query
 * @return {number}
 */
const getInitCursorPosition = query => (query ? query.length : 0);

class Keyboard extends Component {
  state = {
    shift: false,
    alt: false,
    query: this.props.query,
    cursorPosition: getInitCursorPosition(this.props.query),
    selectedLanguage: getInitalLangId(languages, this.props.selectedLanguage),
  };

  defaultFocusedElement = React.createRef();

  componentDidUpdate(_prevProps, prevState) {
    const { onCursorPositionUpdate } = this.props;
    const { cursorPosition, selectedLanguage } = this.state;
    const isLangChanged = selectedLanguage !== prevState.selectedLanguage;

    if (isLangChanged) this.focusDefaultCharacter();

    if (cursorPosition !== prevState.cursorPosition) {
      onCursorPositionUpdate && onCursorPositionUpdate(cursorPosition);
    }
  }

  componentDidMount() {
    this.focusDefaultCharacter();
    document.addEventListener('keydown', this.onKeydown, true);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeydown, true);
  }

  focusDefaultCharacter() {
    const { defaultFocusedElement } = this;
    defaultFocusedElement && SpatialNavigationWrapper.focus(defaultFocusedElement.current);
  }

  onKeydown = ({ keyCode }) => {
    const numberValue = Platform.getKeyCodeNumericValue(keyCode);
    if (numberValue) this.insertCharacter(numberValue);
  };

  onClick = key => {
    const character = this.getDisplayCharacter(key);

    if (key.special) {
      return this.runSpecial(key.special);
    }

    if (character) this.insertCharacter(character);
  };

  onLanguageSelect = selectedLanguage => {
    this.setState({
      selectedLanguage,
    });
  };

  insertCharacter(character) {
    const { query, cursorPosition } = this.state;
    const { maxQuerySize } = this.props;
    const letters = query.split('');

    if (maxQuerySize && query.length >= maxQuerySize) return;

    letters.splice(cursorPosition, 0, character);
    // character could be a "gmail.com" or smth
    this.updateQuery(letters.join(''), cursorPosition + character.length);
  }

  removeCharacter = () => {
    const { query, cursorPosition } = this.state;
    const letters = query.split('');

    if (cursorPosition === 0) {
      return;
    }

    letters.splice(cursorPosition - 1, 1);

    this.updateQuery(letters.join(''), Math.max(0, cursorPosition - 1));
  };

  updateQuery(query, cursorPosition) {
    const { onQueryUpdated } = this.props;

    this.setState({
      query,
      cursorPosition,
    });

    onQueryUpdated && onQueryUpdated(query);
  }

  runSpecial(special) {
    const { showOverlay } = this.props;
    const { selectedLanguage } = this.state;

    switch (special) {
      case 'shift':
        this.setState({ shift: !this.state.shift });
        break;
      case 'alt':
        this.setState({ alt: !this.state.alt });
        break;
      case 'space':
        this.insertCharacter(' ');
        break;
      case 'lang':
        showOverlay({
          type: 'list',
          returnFocusTo: '@KEYBOARD',
          options: {
            listTitle: i18n.t('keyboard.title'),
            items: languages,
            selected: selectedLanguage,
            onSelect: this.onLanguageSelect,
          },
        });
        break;
      case 'left':
        this.moveCursorLeft();
        break;
      case 'right':
        this.moveCursorRight();
        break;
      case 'delete':
        this.removeCharacter();
        break;
      default:
        break;
    }
  }

  renderQuery() {
    const { query, cursorPosition } = this.state;
    const letters = query.split('');

    const result = letters.map((letter, i) => (
      <StyledLetter key={`lt-${letter}-${i}`}>{letter}</StyledLetter>
    ));

    result.splice(cursorPosition, 0, <StyledCursor key={'cursor'} />);

    return result;
  }

  renderRow(row, index) {
    return (
      <Row key={`keyboard-row-${index}`}>
        {row.keys.map((key, index) => {
          const content = this.getKeyContent(key);

          return (
            <Key
              keyMeta={key}
              content={content}
              key={`key-${key.character || ''}-${index}`}
              ref={key.focused && this.defaultFocusedElement}
              onEnter={this.onClick}
            />
          );
        })}
      </Row>
    );
  }

  getKeyContent(key) {
    if (key.icon) return this.getIcon(key.icon);
    return this.getDisplayCharacter(key);
  }

  getIcon(icon) {
    if (icon === 'arrow-left') return <LeftArrowIcon />;
    if (icon === 'arrow-right') return <RightArrowIcon />;
    if (icon === 'input') return <StyledInput>{this.renderQuery()}</StyledInput>;
    if (icon === 'delete') return <DeleteIcon />;
  }

  getDisplayCharacter(key) {
    const { shift, alt } = this.state;

    if (alt) {
      return key.alt || null;
    }

    if (shift) {
      return key.shift || key.character;
    }

    return key.character;
  }

  moveCursorLeft = () => {
    const { cursorPosition } = this.state;

    this.setState({
      cursorPosition: Math.max(0, cursorPosition - 1),
    });
  };

  moveCursorRight = () => {
    const { cursorPosition, query } = this.state;

    this.setState({
      cursorPosition: Math.min(query.length, cursorPosition + 1),
    });
  };

  getLayout() {
    const { selectedLanguage } = this.state;
    const { type } = this.props;
    let layouts;

    switch (type) {
      case 'noEmbeddedInput':
        layouts = noEmbeddedInputLayouts;
        break;
      case 'email':
        layouts = emailLayouts;
        break;
      case 'search':
        layouts = IS_MULTI_LANGUAGE_ALLOWED ? defaultLayouts : searchLayouts;
        break;
      default:
        layouts = defaultLayouts;
    }
    return layouts[selectedLanguage];
  }

  renderLayout() {
    const layout = this.getLayout();

    return layout.map((row, index) => this.renderRow(row, index));
  }

  render() {
    const { selectedLanguage } = this.state;
    const { config, onLeave } = this.props;

    return (
      <>
        <FocusableParent
          key={`keyboard-${selectedLanguage}`}
          sectionName={'KEYBOARD'}
          defaultSection
          config={config}
          onLeave={onLeave}
        >
          {this.renderLayout()}
        </FocusableParent>
      </>
    );
  }
}

Keyboard.propTypes = {
  showOverlay: PropTypes.func.isRequired,
  onLeave: PropTypes.func,
  onQueryUpdated: PropTypes.func,
  onCursorPositionUpdate: PropTypes.func,
  type: PropTypes.oneOf(['noEmbeddedInput', 'email', 'default', 'search']),
  selectedLanguage: PropTypes.oneOf(['en', 'ru', 'uk']),
  query: PropTypes.string,
  maxQuerySize: (props, propName, componentName) => {
    const value = props[propName];
    if (value === undefined || (typeof value === 'number' && value > 0)) return;
    return new Error(
      `Invalid prop \`${propName}\` of value \`${value}\`  supplied to \`${componentName}\`, expected a positive number.`,
    );
  },
};

Keyboard.defaultProps = {
  query: '',
  type: 'default',
  config: {
    straightOnly: false,
    straightOverlapThreshold: 0.1,
  },
};

export default Keyboard;
